import {
  Content,
  ContentQueryParams,
  useContents,
} from "~/api/resources";
import { TagList } from "~/api/resources/Tags";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { PaginationProvider } from "./PaginationProvider";

const STORIES_PER_PAGE = 9;

type StoriesContextType = {
  stories: Content[];
  tags: TagList;
  isLoading: boolean;
  setSearch: (search: string) => void;
  setTagIds: (tagIds: number[] | undefined) => void;
};

const StoriesContext = createContext<StoriesContextType>({
  stories: [],
  tags: {},
  isLoading: false,
  setSearch: () => {},
  setTagIds: () => {},
});

type ProviderProps = PropsWithChildren<{
  initialStories: Content[];
  tags: TagList;
}>;

export const StoriesProvider = ({
  initialStories,
  tags,
  children,
}: ProviderProps) => {
  const [search, setSearchState] = useState<string>();
  const [tagIds, setTagIdsState] = useState<number[]>();

  const queryParams = useMemo<ContentQueryParams>(
    () => ({
      per_page: STORIES_PER_PAGE,
      ...(search ? { search } : {}),
      ...(tagIds?.length ? { filter: { tags: { id: tagIds } } } : {}),
    }),
    [search, tagIds],
  );

  const {
    records,
    data,
    isFetching,
    page,
    fetchNextPage,
    fetchPreviousPage,
  } = useContents("stories", queryParams);

  const setSearch = useCallback((value: string) => {
    setSearchState(value.trim() ? value : undefined);
  }, []);

  const setTagIds = useCallback((ids: number[] | undefined) => {
    setTagIdsState(ids?.length ? ids : undefined);
  }, []);

  const stories =
    !search && !tagIds?.length && page === 1 && !data
      ? initialStories
      : records;

  const totalPages = data?.total_pages ?? 1;

  const value = useMemo(
    () => ({
      stories,
      tags,
      isLoading: isFetching,
      setSearch,
      setTagIds,
    }),
    [stories, tags, isFetching, setSearch, setTagIds],
  );

  return (
    <StoriesContext.Provider value={value}>
      <PaginationProvider
        page={page}
        totalPages={totalPages}
        nextPage={page < totalPages ? fetchNextPage : false}
        prevPage={page > 1 ? fetchPreviousPage : false}
      >
        {children}
      </PaginationProvider>
    </StoriesContext.Provider>
  );
};

export const useStoriesContext = () => useContext(StoriesContext);
